import { useState } from 'react';
import { X, Swords } from 'lucide-react';
import { useAuthenticatedApi } from '@/hooks/useAuthenticatedApi';
import { useToast } from '@/hooks/use-toast';

interface CreateWagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  onWagerCreated: () => void;
}

const CreateWagerModal = ({ isOpen, onClose, user, onWagerCreated }: CreateWagerModalProps) => {
  const [description, setDescription] = useState('');
  const [challengedBroCode, setChallengedBroCode] = useState('');
  const [stakeXp, setStakeXp] = useState(50);
  const [terms, setTerms] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { invokeFunction } = useAuthenticatedApi();
  const { toast } = useToast();

  if (!isOpen) return null;

  const handleCreate = async () => {
    if (!description.trim() || !challengedBroCode.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    if (stakeXp > (user.bro_score || 0)) {
      setError('Insufficient BroScore for this wager');
      return;
    }

    setIsCreating(true);
    setError(null);

    try {
      const data = await invokeFunction('wager-manager', {
        action: 'create',
        challenged_brocode: challengedBroCode.toUpperCase(),
        description: description.trim(),
        stake_amount: stakeXp,
        stake_currency: 'BRO_SCORE',
        terms: terms.trim()
      });

      if (!data.success) {
        throw new Error(data.error || 'Failed to create wager');
      }

      toast({
        title: "Wager Created!",
        description: `Successfully challenged ${challengedBroCode.toUpperCase()} for ${stakeXp} BroScore`,
      });

      // Reset form
      setDescription('');
      setChallengedBroCode('');
      setStakeXp(50);
      setTerms('');
      
      onWagerCreated();
      onClose();
    } catch (error: any) {
      console.error('Error creating wager:', error);
      setError(error.message || 'Failed to create wager');
      toast({
        title: "Error",
        description: error.message || 'Failed to create wager',
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-navy border border-tan rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-tan-dark">
          <div className="flex items-center">
            <Swords className="text-tan mr-2" size={20} />
            <h2 className="text-tan font-bold text-lg">Create Challenge</h2>
          </div>
          <button 
            onClick={onClose}
            className="text-tan-dark hover:text-tan transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <div className="p-4 space-y-4">
          {error && (
            <div className="bg-red-500/20 border border-red-500 rounded p-3">
              <p className="text-red-300 text-sm">{error}</p>
            </div>
          )}

          {/* Challenge Description */}
          <div>
            <label className="block text-tan font-medium mb-2">
              Challenge Description *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your challenge (e.g., 'First to post a viral BroMentum tweet')"
              className="w-full bg-navy-light border border-tan-dark rounded px-3 py-2 text-tan placeholder-tan-dark focus:border-tan focus:outline-none"
              rows={3}
            />
          </div>

          {/* Challenged BroCode */}
          <div>
            <label className="block text-tan font-medium mb-2">
              Opponent's BroCode *
            </label>
            <input
              type="text"
              value={challengedBroCode}
              onChange={(e) => setChallengedBroCode(e.target.value.toUpperCase())}
              placeholder="Enter BroCode (e.g., BRO123)"
              className="w-full bg-navy-light border border-tan-dark rounded px-3 py-2 text-tan placeholder-tan-dark focus:border-tan focus:outline-none"
            />
          </div>

          {/* Stake Amount */}
          <div>
            <label className="block text-tan font-medium mb-2">
              Stake Amount (BroScore)
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="1"
                max={user.bro_score || 0}
                value={stakeXp}
                onChange={(e) => setStakeXp(parseInt(e.target.value) || 0)}
                className="flex-1 bg-navy-light border border-tan-dark rounded px-3 py-2 text-tan focus:border-tan focus:outline-none"
              />
              <span className="text-tan-dark text-sm">
                / {user.bro_score || 0} available
              </span>
            </div>
          </div>

          {/* Terms & Conditions */}
          <div>
            <label className="block text-tan font-medium mb-2">
              Terms & Conditions
            </label>
            <textarea
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
              placeholder="Optional: Add specific rules or conditions for this challenge"
              className="w-full bg-navy-light border border-tan-dark rounded px-3 py-2 text-tan placeholder-tan-dark focus:border-tan focus:outline-none"
              rows={2}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4">
            <button
              onClick={onClose}
              className="flex-1 bg-navy-light border border-tan-dark text-tan py-3 rounded hover:bg-tan-dark/20 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={isCreating || !description.trim() || !challengedBroCode.trim()}
              className="flex-1 bg-tan text-navy py-3 rounded font-medium hover:bg-tan-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? 'Creating...' : 'Create Challenge'}
            </button>
          </div>

          {/* Info */}
          <div className="text-tan-dark text-xs space-y-1 pt-2 border-t border-tan-dark/30">
            <div>• Both parties stake the same amount</div>
            <div>• Winner takes both stakes</div>
            <div>• Challenge expires in 7 days if not accepted</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateWagerModal;